import { useId } from 'react';
import classnames from 'classnames';
import { PaymentMethodProps } from './PaymentMethod';
import Theme from '../../Theme';
import { Radio } from '@mui/material';
import { AlertCircle } from 'lucide-react';
import usePaymentFlow from '../../../hooks/payment/usePaymentFlow';
import { useTranslation } from 'react-i18next';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import { PaymentMethod } from '../../../services/exports/Constants';
import useTheme from '../../../hooks/ui/useTheme';
import { STRIPE_RADIO_GEOMETRY, STRIPE_ROW_METRICS, stripeRowTokens } from '../stripe-elements/appearance';

export enum Variant {
  Default = 'default',
  Adyen = 'adyen',
  /** A row that reads as one of the Payment Element's accordion items (the Stripe Elements checkout). */
  StripeElements = 'stripe_elements',
}

type Props = PaymentMethodProps;

type VariantProps = PaymentMethodProps & {
  disabled: boolean;
  globallyDisabled: boolean;
};

export default function PaymentMethodOption({ variant, isDisabled, ...rest }: Props) {
  const VariantComponent =
    {
      [Variant.Adyen]: AdyenVariant,
      [Variant.StripeElements]: StripeElementsVariant,
    }[variant] ?? DefaultVariant;

  const { isPaymentMethodDisabled } = usePaymentFlow();

  const globallyDisabled = isPaymentMethodDisabled(rest.id);
  const disabled = isDisabled || globallyDisabled;

  return <VariantComponent disabled={disabled} globallyDisabled={globallyDisabled} {...rest} />;
}

function DefaultVariant({
  name,
  id,
  extra,
  isSelected,
  disabled,
  globallyDisabled,
  onClick,
  content,
  children,
  className,
}: VariantProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Payment:PaymentMethods:PaymentMethodOption' });

  const { localData } = useOrderFlow();

  return (
    <div>
      {/*
        Instrumented on both variants. Coverage is NOT symmetric between gateways, and the
        difference matters:
          * Stripe UI — every method (cash, card, PayPal, pay-by-bank) renders through here.
          * Adyen UI — only cash and PayPal. Card, Apple Pay, Google Pay, pay-by-bank and stored
            methods come from `dropin.mount()`, i.e. Adyen-owned markup with no data-test. That is
            the plan's C3 exclusion, not an oversight — do not expect payment-method-card here on
            an Adyen company.
        `data-selected` / `data-disabled` are exposed because the visual state lives in Tailwind
        classes and Adyen's own class names, neither of which is a stable selector. (DefaultVariant
        also has a real MUI <Radio checked>, so getByRole('radio', {checked:true}) works there.)
      */}
      <div
        data-test={`payment-method-${id}`}
        data-selected={isSelected ? 'true' : 'false'}
        data-disabled={disabled ? 'true' : 'false'}
        className={classnames(
          'w-full p-sm cursor-pointer transition-colors ease-in-out delay-150 duration-300 border has-[:checked]:bg-blue-200 dark:has-[:checked]:bg-black-100 has-[:checked]:border-blue-300 dark:has-[:checked]:border-brand-border',
          className,
        )}
        onClick={disabled ? null : onClick}
      >
        <div className={classnames({ 'opacity-35': globallyDisabled })}>
          <div className="flex justify-between gap-lg">
            <div className="flex">
              <Radio
                checked={isSelected}
                sx={{
                  color: Theme.color.blue['300'],
                  '&.Mui-checked': {
                    color: Theme.color.blue['300'],
                  },
                }}
                className="my-auto"
              />
              <p className="ml-sm my-auto text-sm">{name}</p>
            </div>
            {extra}
          </div>
          {globallyDisabled ? (
            <DisabledNotice
              message={t(`feedback.${id === PaymentMethod.Cash ? 'cash_disabled' : 'payment_method_disabled'}`, {
                paymentMethod: name,
                orderMethod: t(`order_methods.${localData.method}`),
              })}
            />
          ) : (
            content
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function AdyenVariant({
  name,
  id,
  extra,
  isSelected,
  disabled,
  globallyDisabled,
  onClick,
  children,
  className,
}: VariantProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Payment:PaymentMethods:PaymentMethodOption' });

  const { localData } = useOrderFlow();

  return (
    <div
      className={classnames('mt-md adyen-checkout__payment-method', {
        'adyen-checkout__payment-method--selected': isSelected,
        'adyen-checkout__payment-method--loading !opacity-75': disabled,
      })}
    >
      <div
        data-test={`payment-method-${id}`}
        data-selected={isSelected ? 'true' : 'false'}
        data-disabled={disabled ? 'true' : 'false'}
        className={classnames('w-full p-md rounded-[8px] cursor-pointer', className)}
        onClick={disabled ? null : onClick}
      >
        <div className={classnames({ 'opacity-35': globallyDisabled })}>
          <div className="flex gap-sm items-center">
            <span
              aria-hidden="true"
              className={classnames('adyen-checkout__payment-method__radio', {
                'adyen-checkout__payment-method__radio--selected': isSelected,
              })}
            />
            <span className="adyen-checkout-brand-wrapper adyen-checkout__payment-method__image__wrapper adyen-checkout__payment-method__image__wrapper--outline">
              {extra}
            </span>
            <p className="my-auto font-medium">{name}</p>
          </div>
          {globallyDisabled && (
            <DisabledNotice
              message={t(`feedback.${id === PaymentMethod.Cash ? 'cash_disabled' : 'payment_method_disabled'}`, {
                paymentMethod: name,
                orderMethod: t(`order_methods.${localData.method}`),
              })}
            />
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

/**
 * The look of a Payment Element accordion item, for the rows the Stripe Elements checkout draws
 * itself (cash, PayPal, the card on file): the same 16px radio, 1px hairline, 12px padding and
 * selection colours the Element gets from its appearance (see stripe-elements/appearance.ts), so
 * the list reads as one. The logo sits at the end, as the Element is told to place it.
 */
function StripeElementsVariant({
  name,
  id,
  extra,
  isSelected,
  disabled,
  globallyDisabled,
  onClick,
  content,
  children,
  className,
}: VariantProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Payment:PaymentMethods:PaymentMethodOption' });

  const { localData } = useOrderFlow();

  return (
    <div>
      <div
        data-test={`payment-method-${id}`}
        data-selected={isSelected ? 'true' : 'false'}
        data-disabled={disabled ? 'true' : 'false'}
        className={classnames(
          stripeElementsRow,
          { [stripeElementsRowSelected]: isSelected, 'opacity-60': disabled && !globallyDisabled },
          className,
        )}
        onClick={disabled ? null : onClick}
      >
        <div className={classnames({ 'opacity-35': globallyDisabled })}>
          <div className="flex items-center gap-sm">
            <StripeElementsRadio isSelected={isSelected} />
            <p className={stripeElementsLabel}>{name}</p>
            <span className={stripeElementsExtra}>{extra}</span>
          </div>
          {globallyDisabled ? (
            <DisabledNotice
              message={t(`feedback.${id === PaymentMethod.Cash ? 'cash_disabled' : 'payment_method_disabled'}`, {
                paymentMethod: name,
                orderMethod: t(`order_methods.${localData.method}`),
              })}
            />
          ) : (
            content
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

function DisabledNotice({ message }: { message: string }) {
  return (
    <div className={disabledNoticeContainer}>
      <AlertCircle className="w-4 h-4 text-brand-primary shrink-0 mt-[2px]" />
      <p className="text-xs leading-relaxed text-brand-text-primary">{message}</p>
    </div>
  );
}

const disabledNoticeContainer =
  'mt-sm flex items-start gap-[10px] rounded-lg border border-brand-primary/30 bg-brand-primary/10 px-[14px] py-[10px]';

/**
 * Stripe's own radio, redrawn from the same tokens and geometry its appearance uses (appearance.ts):
 * the ring clipped to its circle so only the inner half of the stroke shows, and when checked a blue
 * ring around Stripe's dot.
 */
function StripeElementsRadio({ isSelected }: { isSelected: boolean }) {
  const theme = useTheme();
  const tokens = stripeRowTokens(theme.isDarkMode);
  const clipId = useId();
  const { viewBox, ringRadius, ringStrokeWidth, dotRadius } = STRIPE_RADIO_GEOMETRY;
  const center = viewBox / 2;

  return (
    <svg
      aria-hidden="true"
      data-test="stripe-elements-radio"
      width={STRIPE_ROW_METRICS.radioPx}
      height={STRIPE_ROW_METRICS.radioPx}
      viewBox={`0 0 ${viewBox} ${viewBox}`}
      className="shrink-0"
    >
      <defs>
        <clipPath id={clipId}>
          <circle cx={center} cy={center} r={ringRadius} />
        </clipPath>
      </defs>
      <circle
        cx={center}
        cy={center}
        r={ringRadius}
        clipPath={`url(#${clipId})`}
        fill={tokens.background}
        stroke={isSelected ? tokens.radioChecked : tokens.radio}
        strokeWidth={ringStrokeWidth}
      />
      {isSelected && <circle cx={center} cy={center} r={dotRadius} fill={tokens.radioChecked} />}
    </svg>
  );
}

// The classes mirror STRIPE_ROW_METRICS / STRIPE_ROW_TOKENS (appearance.ts) on the Theme colours the
// tokens are built from; StripeElementsAppearance.test pins the two together.
export const stripeElementsRow =
  'w-full p-sm rounded-[12px] border border-gray dark:border-brand-border bg-white dark:bg-black cursor-pointer transition-colors ease-in-out duration-200';
export const stripeElementsRowSelected = '!border-blue-300 bg-blue-200 dark:bg-black-100';
export const stripeElementsLabel = 'my-auto shrink-0 text-sm font-medium leading-5 text-brand-text-primary';
// Gives way before the label and the radio do: a long saved PayPal email truncates inside it
// (PaymentDetails) instead of pushing the remove button out of the row.
const stripeElementsExtra = 'ml-auto flex min-w-0 items-center justify-end';
